<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else-if="checkedOut">
            <div class="row">
                <div class="col-lg-9 col-md-12">
                    <div class="card mb-5">
                        <div class="card-body">
                            This pre-qualification form is currently checked out by {{this.checkedOutUser}} until {{this.checkedOutUntil}}.
                            <div v-if="this.$store.state.user.is_admin">
                                <button class="btn btn-light" @click="removeCheckOut(true)">
                                    Click here
                                </button> to remove the current user's checkout. This will NOT take the user away from the page, it will only remove the lock.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <vue-headful :title="(subcontractor.subcontractor_id ? 'Edit Subcontractor: ' + subcontractor.subcontractor_id : 'New Subcontractor') + ' :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <template v-if="this.$store.state.user.subcontractor_id">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link to="/">
                                <i class="fa fa-id-badge"></i> Dashboard
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link to="/subcontractors/view">
                                {{ subcontractor.subcontractor_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link to="/subcontractors/qualification/view">
                                <i class="fa fa-check-square"></i> Pre-Qualification
                            </router-link>
                        </li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/subcontractors`">
                                <i class="fa fa-id-badge"></i> Subcontractors
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/subcontractors/view/${subcontractor.subcontractor_id}`">
                                {{ subcontractor.subcontractor_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/subcontractors/qualification/view/${subcontractor.subcontractor_id}`">
                                <i class="fa fa-check-square"></i> Pre-Qualification
                            </router-link>
                        </li>
                    </template>
                    <li class="breadcrumb-item active">
                        Edit
                    </li>
                    <li class="ml-3">
                        <router-link
                            :to="reviewSubmitURL"
                            class="btn btn-sm btn-success"
                            :disabled="subcontractor.status_id == 2 || subcontractor.status_id == 8"
                            tag="button"
                            :title="(subcontractor.status_id == 2 || subcontractor.status_id == 8) ? 'Your pre-qualification is already in review. Just hit any of the save buttons and your data will be updated.' : ''">
                            Review & Submit <i class="fa fa-angle-right"></i>
                        </router-link>
                    </li>
                </ol>
            </h4>

            <!-- In Review or Renewal In Review -->
            <div class="alert alert-warning" v-if="subcontractor.status_id == 2 || subcontractor.status_id == 8">
                Your pre-qualification is already in review. Just hit any of the save buttons and your data will be updated. You do not need to re-submit your pre-qualification.
            </div>

            <div class="row">
                <div class="col-lg-9 col-md-12">
                    <div class="card mb-5" id="general-information">
                        <h4 class="card-header">Section 1: General Information</h4>
                        <div class="card-body">

                            <div v-if="settingIsOn(1, 'pre_qual_3rd_question_show_hide')" v-bind:class="{ 'form-group row': true, 'text-danger': errors.prequalified_3rd_party }">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right text-muted">
                                    Are you prequalified through a 3rd party:
                                    <i v-if="settingIsOn(1, 'pre_qual_3rd_question_req') && (subcontractor.prequalified_3rd_party === null || subcontractor.prequalified_3rd_party === undefined)" class="fa fa-asterisk small text-danger"></i><br>
                                    <small>Such as Browz, Avetta, or other?</small>
                                </label>
                                <div class="col-sm-8">
                                    <b-form-radio-group plain v-model="subcontractor.prequalified_3rd_party" class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.prequalified_3rd_party" class="text-danger">
                                        <span v-for="error in errors.prequalified_3rd_party">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-if="subcontractor.prequalified_3rd_party == 1 && settingIsOn(1, 'pre_qual_3rd_file_show_hide')" class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.prequalified_file_id, 'text-muted': !errors.prequalified_file_id }">
                                   Prequalified 3rd Party Certification:
                                   <i v-if="subcontractor.prequalified_3rd_party == 1 && !subcontractor.prequalified_file_id && settingIsOn(1, 'pre_qual_3rd_file_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <template v-if="subcontractor.prequalified_file_id">
                                        <div class="mb-2">
                                            <div>{{ subcontractor.prequalified_file.file_name }}</div>
                                            <span v-if="!subcontractor.prequalified_file.download_only">
                                                <a :href="`/files/view/${subcontractor.prequalified_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                            </span>
                                            <a :href="`/files/download/${subcontractor.prequalified_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" @click="deletePrequalifiedFile" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" v-on:click="uploadPrequalifiedFile=true">Upload New Prequalified 3rd Party Certification</a>
                                        </div>
                                    </template>
                                    <file-uploader v-if="uploadPrequalifiedFile || !subcontractor.prequalified_file_id"
                                        assetType="SubPreQual"
                                        :assetId="subcontractor.subcontractor_id"
                                        :postUrl="`subcontractors/files/prequalified/${subcontractor.subcontractor_id}`"
                                        @fileUploaded="updatePrequalifiedFile" />

                                    <div v-if="errors.prequalified_file_id" class="text-danger mt-2">
                                        <span v-for="error in errors.prequalified_file_id">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[1] && custom_field_sections[1].fields.length > 0">
                               <custom-field-form
                                   :fields="custom_field_sections[1].fields"
                                   :initialValues="(custom_fields[1] ? custom_fields[1] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(1, ...arguments)" />
                            </template>
                            <hr />

                            <bid-list :subcontractorId="subcontractor.subcontractor_id" :editMode="true" />

                            <div class="text-danger mt-2" v-if="errors.bids">
                                <span v-for="error in errors.bids">{{ error }}</span>
                            </div>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(1)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save General Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="company-information">
                        <h4 class="card-header">Section 2: Company Information</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subcontractor_name }">Company Name: <i v-if="!subcontractor.subcontractor_name" class="fa fa-asterisk small text-danger"></i>
                                    <br>
                                    <small>This must match the name on the W9 provided. Enter differing or other names below.</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.subcontractor_name" type="text" placeholder="Enter Company Name" :class="{'is-invalid': errors.subcontractor_name}"></b-form-input>

                                    <span v-if="errors.subcontractor_name" class="text-danger">
                                        <span v-for="error in errors.subcontractor_name">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_other_names }">
                                    Other Names: <i v-if="subcontractor.has_other_names === null || subcontractor.has_other_names === undefined" class="fa fa-asterisk small text-danger"></i><br><small>Has the company operated under other names?</small>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-radio-group plain v-model="subcontractor.has_other_names" class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_other_names" class="text-danger">
                                        <span v-for="error in errors.has_other_names">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row" v-if="subcontractor.has_other_names == 1">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.other_names }">
                                    List Other Names: <i v-if="!subcontractor.other_names" class="fa fa-asterisk small text-danger"></i><br><small>Clarify whether other name is former name, affiliate, subsidiary, etc.</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.other_names" type="text" placeholder="List all other Company Names" :class="{'is-invalid': errors.other_names}" rows="3"></b-form-textarea>

                                    <span v-if="errors.other_names" class="text-danger">
                                        <span v-for="error in errors.other_names">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.address }">Address: <i v-if="!subcontractor.address" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.address" type="text" placeholder="Enter Company Address" :class="{'is-invalid': errors.address}"></b-form-input>

                                    <span v-if="errors.address" class="text-danger">
                                        <span v-for="error in errors.address">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.city }">City: <i v-if="!subcontractor.city" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.city" type="text" placeholder="Enter Company City" :class="{'is-invalid': errors.city}"></b-form-input>

                                    <span v-if="errors.city" class="text-danger">
                                        <span v-for="error in errors.city">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.state_id }">State: <i v-if="!subcontractor.state_id" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <select v-model="subcontractor.state_id" class="form-control" :class="{'is-invalid': errors.state_id}">
                                        <option :value="null">-- Select a State --</option>
                                        <option v-for="state in state_list" :value="state.state_id">{{ state.state_code }}</option>
                                    </select>

                                    <span v-if="errors.state_id" class="text-danger">
                                        <span v-for="error in errors.state_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.zip }">Zip: <i v-if="!subcontractor.zip" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.zip" type="text" placeholder="Enter Zip Code" :class="{'is-invalid': errors.zip}" style="max-width: 150px;"></b-form-input>

                                    <span v-if="errors.zip" class="text-danger">
                                        <span v-for="error in errors.zip">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <hr/>

                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.company_contact_name }">
                                   Company Contact Name: <i v-if="!subcontractor.company_contact_name" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.company_contact_name" :class="{'is-invalid': errors.company_contact_name}" placeholder="Enter Name"></b-form-input>

                                    <span v-if="errors.company_contact_name" class="text-danger">
                                        <span v-for="error in errors.company_contact_name">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.company_contact_phone }">
                                   Company Contact Phone: <i v-if="!subcontractor.company_contact_phone" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.company_contact_phone" :class="{'is-invalid': errors.company_contact_phone}" placeholder="Enter Phone Number"></b-form-input>

                                    <span v-if="errors.company_contact_phone" class="text-danger">
                                        <span v-for="error in errors.company_contact_phone">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.company_contact_email }">
                                   Company Contact Email: <i v-if="!subcontractor.company_contact_email" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="email" v-model="subcontractor.company_contact_email" :class="{'is-invalid': errors.company_contact_email}" placeholder="Enter Email"></b-form-input>

                                    <span v-if="errors.company_contact_email" class="text-danger">
                                        <span v-for="error in errors.company_contact_email">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <hr/>

                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.phone }">Phone: <i v-if="!subcontractor.phone" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.phone" type="text" placeholder="Enter Phone " :class="{'is-invalid': errors.phone}"></b-form-input>

                                    <span v-if="errors.phone" class="text-danger">
                                        <span v-for="error in errors.phone">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.fax }">Fax:</label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input v-model="subcontractor.fax" type="text" placeholder="Enter Fax" :class="{'is-invalid': errors.fax}"></b-form-input>

                                    <span v-if="errors.fax" class="text-danger">
                                        <span v-for="error in errors.fax">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.office_type_id }">Office Type: <i v-if="!subcontractor.office_type_id" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-select v-model="subcontractor.office_type_id" :options="officeTypes" :class="{'is-invalid': errors.office_type_id}" />

                                    <span v-if="errors.office_type_id" class="text-danger">
                                        <span v-for="error in errors.office_type_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row" v-if="subcontractor.office_type_id == 4">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.office_type_other }">Other Office Type:</label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.office_type_other" :class="{'is-invalid': errors.office_type_other}" placeholder="Enter Other Office Type"></b-form-input>

                                    <span v-if="errors.office_type_other" class="text-danger">
                                        <span v-for="error in errors.office_type_other">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <hr/>

                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.business_start_date }">Business Start Date: <i v-if="!business_start_date" class="fa fa-asterisk small text-danger"></i>
                                    <br>
                                    <small>Or best approximation</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <date-picker v-model="business_start_date" :config="{format: 'MM/YYYY', useCurrent: false, viewMode: 'months'}" :class="{ 'is-invalid': errors.business_start_date }" placeholder="Start of Business"></date-picker>

                                    <span v-if="errors.business_start_date" class="text-danger">
                                        <span v-for="error in errors.business_start_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.parent_company }">
                                    Parent Company <br>
                                    <small>List name &amp; address (If applicable)</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.parent_company" type="text" placeholder="Parent Company Name and Address" :class="{'is-invalid': errors.parent_company}" rows="3"></b-form-textarea>

                                    <span v-if="errors.parent_company" class="text-danger">
                                        <span v-for="error in errors.parent_company">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.website }">Website:</label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.website" :class="{'is-invalid': errors.website}" placeholder="Enter Company Website"></b-form-input>

                                    <span v-if="errors.website" class="text-danger">
                                        <span v-for="error in errors.website">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <hr/>

                            <div class="mt-2">
                                <sub-designation-list :subcontractorId="subcontractor.subcontractor_id" :editMode="true" />
                            </div>
                            <hr/>

                            <div class="mt-2">
                                <sub-license-list  :subcontractorId="subcontractor.subcontractor_id" :editMode="true" />
                            </div>

                            <template v-if="custom_field_sections[2] && custom_field_sections[2].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[2].fields"
                                   :initialValues="(custom_fields[2] ? custom_fields[2] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(2, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(2)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Company Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="work-performed">
                        <h4 class="card-header">Section 3: Work Performed</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subTrades }">
                                   Trades Performed: <i v-if="!subcontractor.subTrades || subcontractor.subTrades.length === 0" class="fa fa-asterisk text-danger small"></i><br>
                                   <small>Check all that apply</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-checkbox-group plain stacked v-model="subcontractor.subTrades" :options="trades"></b-form-checkbox-group>

                                    <span v-if="errors.subTrades" class="text-danger">
                                        <span v-for="error in errors.subTrades">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.other_trades }">Please list specific sub-category:</label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.other_trades" :class="{'is-invalid': errors.other_trades}" placeholder="Enter specific sub-category of trade code selected above"></b-form-input>

                                    <span v-if="errors.other_trades" class="text-danger">
                                        <span v-for="error in errors.other_trades">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <hr/>

                            <template v-if="settingIsOn(3, 'areas_of_work_show_hide')">
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subAreas }">
                                       Geographic Areas Willing to Work:
                                       <i v-if="!subcontractor.subAreas || subcontractor.subAreas.length === 0 && settingIsOn(3, 'areas_of_work_req')" class="fa fa-asterisk text-danger small"></i><br>
                                       <small>Check all that apply</small>
                                    </label>
                                    <div class="col-md-8 col-sm-8">

                                        <b-form-checkbox-group plain stacked v-model="subcontractor.subAreas" :options="areas"></b-form-checkbox-group>

                                        <span v-if="errors.subAreas" class="text-danger">
                                            <span v-for="error in errors.subAreas">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.other_areas }">Other Areas:</label>
                                    <div class="col-md-8 col-sm-8">

                                        <b-form-input type="text" v-model="subcontractor.other_areas" :class="{'is-invalid': errors.other_areas}" placeholder="Enter Other Areas if not listed above"></b-form-input>

                                        <span v-if="errors.other_areas" class="text-danger">
                                            <span v-for="error in errors.other_areas">{{ error }}</span>
                                        </span>
                                    </div>
                                </div>
                                <hr/>
                            </template>

                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subRegistrations }">
                                   State Registrations: <i v-if="sub_registrations.length === 0" class="fa fa-asterisk text-danger small"></i><br>
                                   <small>Check all states in which your company is registered to do business</small>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <table class="table table-bordered">
                                        <thead class="bg-light">
                                            <tr>
                                                <th>
                                                    <i class="fa fa-edit"></i>
                                                    / <i class="fa fa-trash text-danger"></i>
                                                </th>
                                                <th style="min-width: 150px;">State</th>
                                                <th>Registration# or Note</th>
                                                <th>Copy of Registration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="(reg, i) in sub_registrations">
                                                <tr v-if="edit_state_registration.id !== reg.id">
                                                    <td>
                                                        <a href="javascript:void(0)" @click="editSubStateRegistration(reg)" :disabled="edit_state_reg_loading"><i class="fa fa-edit"></i></a>
                                                        <a href="javascript:void(0)" @click="deleteSubStateRegistration(reg, i)" :disabled="edit_state_reg_loading" v-if="reg.id != null"><i class="fa fa-trash text-danger"></i></a>
                                                    </td>
                                                    <td>
                                                        {{ reg.state_name }}
                                                    </td>
                                                    <td>
                                                        {{ reg.registration_notes }}
                                                    </td>
                                                    <td>
                                                        {{ reg.file_name }}
                                                    </td>
                                                </tr>
                                                <tr v-if="edit_state_registration.id === reg.id">
                                                    <td>
                                                        <a href="javascript:void(0)" @click="cancelStateRegistration(i)" :disabled="edit_state_reg_loading" title="Cancel Edit" class="text-muted"><i class="fa fa-times"></i></a>&nbsp;
                                                        <a href="javascript:void(0)" @click="updateStateRegistration(i)" :disabled="edit_state_reg_loading" title="Save State Registration" class="text-success"><i class="fa fa-save"></i></a>
                                                    </td>
                                                    <td>
                                                        <b-form-select v-model="edit_state_registration.state_id" size="sm" :options="stateRegistrationOptions" />
                                                    </td>
                                                    <td>
                                                        <b-form-input v-model="edit_state_registration.registration_notes" size="sm" placeholder="Enter Registration# or Note" />
                                                    </td>
                                                    <td>
                                                        <b-form-file v-model="edit_state_reg_file" size="sm" />
                                                    </td>
                                                </tr>
                                            </template>

                                            <tr>
                                                <td colspan="4" class="text-center">
                                                    <a href="javascript:void(0)"
                                                        @click="addStateRegistration()"
                                                        style="border: 1px dashed #ccc; display: block; padding: 6px 8px;"
                                                        v-if="(typeof edit_state_registration.id == 'undefined')">
                                                        <i class="fa fa-plus-circle text-success"></i> Add State
                                                    </a>
                                                    <a href="javascript:void(0)"
                                                        v-else
                                                        style="border: 1px dashed #ccc; display: block; padding: 6px 8px;">
                                                        <span class="text-muted"><i class="fa fa-plus-circle"></i> Add State</span>
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <span v-if="errors.subRegistrations" class="text-danger">
                                        <span v-for="error in errors.subRegistrations">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.other_bus_registrations }">Other States: <br><small>If your state is not listed above, add it here:</small></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.other_bus_registrations" :class="{'is-invalid': errors.other_bus_registrations}" placeholder="Enter Other States if not listed above"></b-form-input>

                                    <span v-if="errors.other_bus_registrations" class="text-danger">
                                        <span v-for="error in errors.other_bus_registrations">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[3] && custom_field_sections[3].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[3].fields"
                                   :initialValues="(custom_fields[3] ? custom_fields[3] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(3, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(3)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Work Performed
                                </template>
                            </b-button>
                        </div>
                    </div>

                    <div class="card mb-5" id="financial-information">
                        <h4 class="card-header">Section 4: Financial Information</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.comp_structure_id }">Company Structure: <i v-if="!subcontractor.comp_structure_id" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-select v-model="subcontractor.comp_structure_id" :options="companyStructures" :class="{'is-invalid': errors.comp_structure_id}" />

                                    <span v-if="errors.comp_structure_id" class="text-danger">
                                        <span v-for="error in errors.comp_structure_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row" v-if="subcontractor.comp_structure_id == 6">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.comp_structure_other }">Other Structure:</label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.comp_structure_other" :class="{'is-invalid': errors.comp_structure_other}" placeholder="Enter Other Company Structure"></b-form-input>

                                    <span v-if="errors.comp_structure_other" class="text-danger">
                                        <span v-for="error in errors.comp_structure_other">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.db_number }">Dun &amp; Bradstreet Number:</label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-input type="text" v-model="subcontractor.db_number" :class="{'is-invalid': errors.db_number}" placeholder="Enter Dun &amp; Bradstreet Number"></b-form-input>

                                     <span v-if="errors.db_number" class="text-danger">
                                         <span v-for="error in errors.db_number">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.bank_name }">Name of Your Company's Bank:</label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-input type="text" v-model="subcontractor.bank_name" :class="{'is-invalid': errors.bank_name}" placeholder="Enter Name of Company Bank"></b-form-input>

                                     <span v-if="errors.bank_name" class="text-danger">
                                         <span v-for="error in errors.bank_name">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.bank_address }">Bank Address:<br><small>Street, City, State, Zip</small></label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-input type="text" v-model="subcontractor.bank_address" :class="{'is-invalid': errors.bank_address}" placeholder="Enter Bank Address"></b-form-input>

                                     <span v-if="errors.bank_address" class="text-danger">
                                         <span v-for="error in errors.bank_address">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.bank_contact }">Bank Contact Name &amp; Phone:</label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-input type="text" v-model="subcontractor.bank_contact" :class="{'is-invalid': errors.bank_contact}" placeholder="Enter Bank Contact Information"></b-form-input>

                                     <span v-if="errors.bank_contact" class="text-danger">
                                         <span v-for="error in errors.bank_contact">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.company_id_num }">Federal ID# or SS#: <i v-if="!subcontractor.company_id_num" class="fa fa-asterisk small text-danger"></i></label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-input type="text" v-model="subcontractor.company_id_num" :class="{'is-invalid': errors.company_id_num}" placeholder="Enter Federal# of SS#"></b-form-input>

                                     <span v-if="errors.company_id_num" class="text-danger">
                                         <span v-for="error in errors.company_id_num">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.irs_w9 }">
                                    <i class="fa fa-question-circle" title="Latest version available at www.irs.gov under Forms & Instructions"></i> IRS Form W9: <i v-if="!subcontractor.irsw9_id" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                 <div class="col-md-8 col-sm-8">

                                     <template v-if="subcontractor.irsw9_id">
                                        <div class="mb-2">
                                            <div>{{ subcontractor.irs_w9.file_name }}</div>
                                            <span v-if="!subcontractor.irs_w9.download_only">
                                                <a :href="`/files/view/${subcontractor.irs_w9.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                            </span>
                                            <a :href="`/files/download/${subcontractor.irs_w9.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" @click="deleteIRSForm" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" v-on:click="uploadIRSForm=true">Upload New IRS W9 Form</a>
                                        </div>
                                     </template>
                                     <file-uploader v-if="uploadIRSForm || !subcontractor.irsw9_id"
                                        assetType="SubPreQual"
                                        :assetId="subcontractor.subcontractor_id"
                                        :postUrl="`subcontractors/files/irsw9/${subcontractor.subcontractor_id}`"
                                        @fileUploaded="updateIRSForm" />

                                     <span v-if="errors.irs_w9" class="text-danger">
                                         <span v-for="error in errors.irs_w9">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>

                             <template v-if="custom_field_sections[4] && custom_field_sections[4].fields.length > 0">
                                <hr />

                                <custom-field-form
                                    :fields="custom_field_sections[4].fields"
                                    :initialValues="(custom_fields[4] ? custom_fields[4] : null)"
                                    :errors="errors"
                                    labelSize="col-lg-3 col-md-3 col-sm-4"
                                    fieldSize="col-sm-8"
                                    @customFieldsChanged="setCustomFieldValues(4, ...arguments)" />
                             </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(4)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Financial Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="safety-information">
                        <h4 class="card-header">Section 5: Health, Safety & Environmental</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.company_size }">Current Number of Employees: <i v-if="!subcontractor.company_size" class="fa fa-asterisk small text-danger"></i></label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.company_size" :class="{'is-invalid': errors.company_size}" placeholder="Enter Current Number of Employees"></b-form-input>

                                    <span v-if="errors.company_size" class="text-danger">
                                        <span v-for="error in errors.company_size">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.safety_rep_name }">
                                   Company Safety Representative Name: <i v-if="!subcontractor.safety_rep_name" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.safety_rep_name" :class="{'is-invalid': errors.safety_rep_name}" placeholder="Enter Name"></b-form-input>

                                    <span v-if="errors.safety_rep_name" class="text-danger">
                                        <span v-for="error in errors.safety_rep_name">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.hse_credentials }">
                                   List HSE credentials (if any):
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.hse_credentials" :class="{'is-invalid': errors.hse_credentials}" placeholder="Enter HSE Credentials"></b-form-input>

                                    <span v-if="errors.hse_credentials" class="text-danger">
                                        <span v-for="error in errors.hse_credentials">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.years_of_experience }">
                                   Years of Experience: <i v-if="!subcontractor.years_of_experience" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.years_of_experience" :class="{'is-invalid': errors.years_of_experience}" placeholder="Enter Years of Experience"></b-form-input>

                                    <span v-if="errors.years_of_experience" class="text-danger">
                                        <span v-for="error in errors.years_of_experience">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.safety_rep_phone }">
                                   Company Safety Representative Phone: <i v-if="!subcontractor.safety_rep_phone" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.safety_rep_phone" :class="{'is-invalid': errors.safety_rep_phone}" placeholder="Enter Phone Number"></b-form-input>

                                    <span v-if="errors.safety_rep_phone" class="text-danger">
                                        <span v-for="error in errors.safety_rep_phone">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.safety_rep_email }">
                                   Company Safety Representative Email: <i v-if="!subcontractor.safety_rep_email" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="email" v-model="subcontractor.safety_rep_email" :class="{'is-invalid': errors.safety_rep_email}" placeholder="Enter Email"></b-form-input>

                                    <span v-if="errors.safety_rep_email" class="text-danger">
                                        <span v-for="error in errors.safety_rep_email">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_written_program }">
                                    Written Safety &amp; Health Program: <i v-if="subcontractor.has_written_program === null || subcontractor.has_written_program === undefined" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                 <div class="col-md-8 col-sm-8">

                                     <b-form-radio-group v-model="subcontractor.has_written_program" plain class="mt-2">
                                         <b-form-radio value="1">Yes</b-form-radio>
                                         <b-form-radio value="0">No</b-form-radio>
                                     </b-form-radio-group>

                                     <span v-if="errors.has_written_program" class="text-danger">
                                         <span v-for="error in errors.has_written_program">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div v-if="subcontractor.has_written_program == 1 && settingIsOn(5, 'hse_prog_file_show_hide')" class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.program_file_id, 'text-muted': !errors.program_file_id }">
                                    HSE Program File: <i v-if="!subcontractor.program_file_id && settingIsOn(5, 'hse_prog_file_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                 <div class="col-md-8 col-sm-8">

                                     <template v-if="subcontractor.program_file_id">
                                        <div class="mb-2">
                                            <div>{{ subcontractor.program_file.file_name }}</div>
                                            <span v-if="!subcontractor.program_file.download_only">
                                                <a :href="`/files/view/${subcontractor.program_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                            </span>
                                            <a :href="`/files/download/${subcontractor.program_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" @click="deleteProgramFile" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" v-on:click="uploadProgramFile=true">Upload New Program Copy</a>
                                        </div>
                                     </template>
                                     <file-uploader v-if="uploadProgramFile || !subcontractor.program_file_id"
                                        assetType="SubPreQual"
                                        :assetId="subcontractor.subcontractor_id"
                                        :postUrl="`subcontractors/files/program/${subcontractor.subcontractor_id}`"
                                        @fileUploaded="updateProgramFile" />

                                     <span v-if="errors.program_file_id" class="text-danger">
                                         <span v-for="error in errors.program_file_id">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <div class="form-group row">
                                 <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.emr_file_id, 'text-danger': errors.emr_file_id }">
                                    <i class="fa fa-question-circle" title="EMR letter produced by your worker's compensation carrier"></i> EMR Letter: <i v-if="!subcontractor.emr_file_id" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                 <div class="col-md-8 col-sm-8">

                                     <template v-if="subcontractor.emr_file_id">
                                        <div class="mb-2">
                                            <div>{{ subcontractor.emr_file.file_name }}</div>
                                            <span v-if="!subcontractor.emr_file.download_only">
                                                <a :href="`/files/view/${subcontractor.emr_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                            </span>
                                            <a :href="`/files/download/${subcontractor.emr_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" @click="deleteEmrFile" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                            <a href="javascript:void(0)" v-on:click="uploadEmrFile=true">Upload New EMR Letter</a>
                                        </div>
                                     </template>
                                     <file-uploader v-if="uploadEmrFile || !subcontractor.emr_file_id"
                                        assetType="SubPreQual"
                                        :assetId="subcontractor.subcontractor_id"
                                        :postUrl="`subcontractors/files/emr/${subcontractor.subcontractor_id}`"
                                        @fileUploaded="updateEmrFile" />

                                     <span v-if="errors.emr_file_id" class="text-danger">
                                         <span v-for="error in errors.emr_file_id">{{ error }}</span>
                                     </span>
                                 </div>
                             </div>
                             <hr/>

                             <div class="my-4">
                                <sub-osha-years-list :subcontractorId="subcontractor.subcontractor_id" :editMode="true" />
                             </div>
                             <hr/>

                             <div class="my-4">
                                <osha-citation-list :subcontractorId="subcontractor.subcontractor_id" :editMode="true" />
                             </div>

                             <div v-if="editMode" class="form-group">
                                 <b-form-checkbox v-model="subcontractor.has_osha_citations" value="0" unchecked-value="1" plain></b-form-checkbox>
                                 <label>There are no OSHA Violations to report during the indicated time period.</label>
                                 <div v-if="errors.has_osha_citations" class="text-danger">
                                     <span v-for="error in errors.has_osha_citations">{{ error }}</span>
                                 </div>
                             </div>
                             <hr/>

                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subFieldTrainings }">
                                     Field Craft Personnel Training:<br>
                                     <small>Check all that apply</small>
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-checkbox-group plain stacked v-model="subcontractor.subFieldTrainings" :options="fieldTrainings"></b-form-checkbox-group>

                                      <span v-if="errors.subFieldTrainings" class="text-danger">
                                          <span v-for="error in errors.subFieldTrainings">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_safety_orientation }">
                                     Safety Orientation:<br>
                                     <small>For New Employees</small>
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-radio-group v-model="subcontractor.has_safety_orientation" plain class="mt-2">
                                          <b-form-radio value="1">Yes</b-form-radio>
                                          <b-form-radio value="0">No</b-form-radio>
                                      </b-form-radio-group>

                                      <span v-if="errors.has_safety_orientation" class="text-danger">
                                          <span v-for="error in errors.has_safety_orientation">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_person_training }">
                                     Competent Person Training:
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-radio-group v-model="subcontractor.has_person_training" plain class="mt-2">
                                          <b-form-radio value="1">Yes</b-form-radio>
                                          <b-form-radio value="0">No</b-form-radio>
                                      </b-form-radio-group>

                                      <span v-if="errors.has_person_training" class="text-danger">
                                          <span v-for="error in errors.has_person_training">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_safety_meetings }">
                                     Employee Safety Meetings:
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-radio-group v-model="subcontractor.has_safety_meetings" plain class="mt-2">
                                          <b-form-radio value="1">Yes</b-form-radio>
                                          <b-form-radio value="0">No</b-form-radio>
                                      </b-form-radio-group>

                                      <span v-if="errors.has_safety_meetings" class="text-danger">
                                          <span v-for="error in errors.has_safety_meetings">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row" v-if="subcontractor.has_safety_meetings == 1">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.meeting_frequency }">
                                     Frequency of Meetings:
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-radio-group v-model="subcontractor.meeting_frequency" plain class="mt-2">
                                          <b-form-radio value="Daily">Daily</b-form-radio>
                                          <b-form-radio value="Weekly">Weekly</b-form-radio>
                                          <b-form-radio value="Monthly">Monthly</b-form-radio>
                                      </b-form-radio-group>

                                      <span v-if="errors.meeting_frequency" class="text-danger">
                                          <span v-for="error in errors.meeting_frequency">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.meeting_host_title }">
                                     Title of Safety Meeting Host:
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-input type="text" v-model="subcontractor.meeting_host_title" placeholder="Enter the Title of the Staff Member who conducts the meetings" />

                                      <span v-if="errors.meeting_frequency" class="text-danger">
                                          <span v-for="error in errors.meeting_frequency">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.substance_abuse_policy }">
                                     Substance Abuse Policy:
                                 </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-radio-group v-model="subcontractor.substance_abuse_policy" plain class="mt-2">
                                          <b-form-radio value="1">Yes</b-form-radio>
                                          <b-form-radio value="0">No</b-form-radio>
                                      </b-form-radio-group>

                                      <span v-if="errors.substance_abuse_policy" class="text-danger">
                                          <span v-for="error in errors.substance_abuse_policy">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>
                              <div class="form-group row" v-if="subcontractor.substance_abuse_policy == 1">
                                   <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.subTestings }">
                                        Testing Frequency:<br>
                                      <small>Check all that apply</small>
                                  </label>
                                   <div class="col-md-8 col-sm-8">

                                       <b-form-checkbox-group plain stacked v-model="subcontractor.subTestings" :options="substanceTestings"></b-form-checkbox-group>

                                       <span v-if="errors.subTestings" class="text-danger">
                                           <span v-for="error in errors.subTestings">{{ error }}</span>
                                       </span>
                                   </div>
                              </div>
                              <div class="form-group row">
                                  <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.safety_differentiators }">
                                      List any Safety Differentiators:<br>
                                  </label>
                                  <div class="col-md-8 col-sm-8">

                                      <b-form-textarea v-model="subcontractor.safety_differentiators" :class="{'is-invalid': errors.safety_differentiators}" rows="4" placeholder="i.e. OSHA/VPP partnerships; Pre-Task Planning; JSA planning; Behavior Observation program/system; Safety Awards; etc." />

                                      <span v-if="errors.safety_differentiators" class="text-danger">
                                          <span v-for="error in errors.safety_differentiators">{{ error }}</span>
                                      </span>
                                  </div>
                              </div>

                              <template v-if="custom_field_sections[5] && custom_field_sections[5].fields.length > 0">
                                 <hr />

                                 <custom-field-form
                                     :fields="custom_field_sections[5].fields"
                                     :initialValues="(custom_fields[5] ? custom_fields[5] : null)"
                                     :errors="errors"
                                     labelSize="col-lg-3 col-md-3 col-sm-4"
                                     fieldSize="col-sm-8"
                                     @customFieldsChanged="setCustomFieldValues(5, ...arguments)" />
                              </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(5)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Safety Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="insurance-information">
                        <h4 class="card-header">Section 6: Insurance Information</h4>
                        <div class="card-body">
                            <template v-if="settings.insurance_notes">
                                <div class="alert alert-info" style="white-space: pre-wrap;" v-html="settings.insurance_notes"></div>
                            </template>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.insurance_info }">
                                    Insurance Company: <i v-if="!subcontractor.insurance_info" class="fa fa-asterisk text-danger small"></i><br>
                                    <small>Please list name, address, and phone number</small>
                                </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.insurance_info" :class="{'is-invalid': errors.insurance_info}" rows="4" placeholder="Enter Insurance Company Name, Address, and Phone Number" />

                                    <span v-if="errors.insurance_info" class="text-danger">
                                        <span v-for="error in errors.insurance_info">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.insurance_file_id }">
                                    Certificate of Insurance: <i v-if="!subcontractor.insurance_file_id" class="fa fa-asterisk text-danger small"></i><br>
                                    <small>Must reflect current, non-expired insurance coverage.<br>
                                    Can be a "sample" issued to another certificate holder - with certificate holder information redacted.</small>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <template v-if="subcontractor.insurance_file_id">
                                       <div class="mb-2">
                                           <div>{{ subcontractor.insurance_file.file_name }}</div>
                                           <span v-if="!subcontractor.insurance_file.download_only">
                                               <a :href="`/files/view/${subcontractor.insurance_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                           </span>
                                           <a :href="`/files/download/${subcontractor.insurance_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" @click="deleteInsuranceFile" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" v-on:click="uploadInsuranceFile=true">Upload New Certificate</a>
                                       </div>
                                    </template>
                                    <file-uploader v-if="uploadInsuranceFile || !subcontractor.insurance_file_id"
                                       assetType="SubPreQual"
                                       :assetId="subcontractor.subcontractor_id"
                                       :postUrl="`subcontractors/files/insurance/${subcontractor.subcontractor_id}`"
                                       @fileUploaded="updateInsuranceFile" />

                                    <span v-if="errors.insurance_file_id" class="text-danger">
                                        <span v-for="error in errors.insurance_file_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[6] && custom_field_sections[6].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[6].fields"
                                   :initialValues="(custom_fields[6] ? custom_fields[6] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(6, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(6)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Insurance Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="bonding-information">
                        <h4 class="card-header">Section 7: Bonding Information</h4>
                        <div class="card-body">
                            <template v-if="settings.sub_bonding_notes">
                                <div class="alert alert-info" style="white-space: pre-wrap;" v-html="settings.sub_bonding_notes"></div>
                            </template>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_company, 'text-danger': errors.bonding_company }">
                                    Bonding Company: <i v-if="!subcontractor.bonding_company && settingIsOn(7, 'bonding_company_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-input type="text" v-model="subcontractor.bonding_company" :class="{'is-invalid': errors.bonding_company}" placeholder="Enter Bonding Company Name" />
                                    <span v-if="errors.bonding_company" class="text-danger">
                                        <span v-for="error in errors.bonding_company">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_file_id, 'text-danger': errors.bonding_file_id }">
                                    Copy of Bonding Letter: <i v-if="!subcontractor.bonding_file_id && settingIsOn(7, 'bonding_file_id_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <template v-if="subcontractor.bonding_file_id">
                                       <div class="mb-2">
                                           <div>{{ subcontractor.bonding_file.file_name }}</div>
                                           <span v-if="!subcontractor.bonding_file.download_only">
                                               <a :href="`/files/view/${subcontractor.bonding_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                           </span>
                                           <a :href="`/files/download/${subcontractor.bonding_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" @click="deleteBondingLetter" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" v-on:click="uploadBondingFile=true">Upload Copy of Bonding Letter</a>
                                       </div>
                                    </template>
                                    <file-uploader v-if="uploadBondingFile || !subcontractor.bonding_file_id"
                                        assetType="SubPreQual"
                                       :assetId="subcontractor.subcontractor_id"
                                       :postUrl="`subcontractors/files/bonding/${subcontractor.subcontractor_id}`"
                                       @fileUploaded="updateBondingLetter" />

                                    <span v-if="errors.bonding_file_id" class="text-danger">
                                        <span v-for="error in errors.bonding_file_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_agent_firm, 'text-danger': errors.bonding_agent_firm }">
                                    Bonding Agent &amp; Firm: <i v-if="!subcontractor.bonding_agent_firm && settingIsOn(7, 'bonding_agent_firm_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-input type="text" v-model="subcontractor.bonding_agent_firm" :class="{'is-invalid': errors.bonding_agent_firm}" placeholder="Enter Bonding Agent and Firm" />
                                    <span v-if="errors.bonding_agent_firm" class="text-danger">
                                        <span v-for="error in errors.bonding_agent_firm">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_phone, 'text-danger': errors.bonding_phone }">
                                    Phone: <i v-if="!subcontractor.bonding_phone && settingIsOn(7, 'bonding_phone_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-input type="text" v-model="subcontractor.bonding_phone" :class="{'is-invalid': errors.bonding_phone}" placeholder="Enter Bonding Phone" />
                                    <span v-if="errors.bonding_phone" class="text-danger">
                                        <span v-for="error in errors.bonding_phone">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_max_capacity, 'text-danger': errors.bonding_max_capacity }">
                                    Max. Overall Bonding Capacity: <i v-if="!subcontractor.bonding_max_capacity && settingIsOn(7, 'bonding_max_capacity_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-input type="text" v-model="subcontractor.bonding_max_capacity" :class="{'is-invalid': errors.bonding_max_capacity}" placeholder="Enter Overall Maximum Bonding Capacity" />
                                    <span v-if="errors.bonding_max_capacity" class="text-danger">
                                        <span v-for="error in errors.bonding_max_capacity">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_max_contract, 'text-danger': errors.bonding_max_contract}">
                                    Max. Bonding Capacity Per Contract: <i v-if="!subcontractor.bonding_max_contract && settingIsOn(7, 'bonding_max_contract_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-input type="text" v-model="subcontractor.bonding_max_contract" :class="{'is-invalid': errors.bonding_max_contract}" placeholder="Enter Maximum Bonding Capacity Per Contract" />
                                    <span v-if="errors.bonding_max_contract" class="text-danger">
                                        <span v-for="error in errors.bonding_max_contract">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_start_date, 'text-danger': errors.bonding_start_date}">
                                    Bonding Company Start Date: <i v-if="!subcontractor.bonding_start_date && settingIsOn(7, 'bonding_start_date_req')" class="fa fa-asterisk small text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <date-picker v-model="bonding_start_date" :config="{format: 'MM/DD/YYYY', useCurrent: false, viewMode: 'months'}" :class="{ 'is-invalid': errors.bonding_start_date }" placeholder="Date Company started with this Bonding Company"></date-picker>
                                    <span v-if="errors.bonding_start_date" class="text-danger">
                                        <span v-for="error in errors.bonding_start_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.bonding_previous_companies, 'text-danger': errors.bonding_previous_companies}">
                                    Previous Bonding Companies:  <i v-if="!subcontractor.bonding_previous_companies && settingIsOn(7, 'bonding_previous_companies_req')" class="fa fa-asterisk small text-danger"></i><br>
                                    <small>List all previous bonding companies</small>
                                </label>
                                <div class="col-md-8 col-sm-8">
                                    <b-form-textarea v-model="subcontractor.bonding_previous_companies" :class="{'is-invalid': errors.bonding_previous_companies}" rows="3" placeholder="Enter Previous Bonding Companies"></b-form-textarea>
                                    <span v-if="errors.bonding_previous_companies" class="text-danger">
                                        <span v-for="error in errors.bonding_previous_companies">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[7] && custom_field_sections[7].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[7].fields"
                                   :initialValues="(custom_fields[7] ? custom_fields[7] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(7, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(7)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Bonding Information
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="past-performance">
                        <h4 class="card-header">Section 8: Past Performance</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_official_complaint }">
                                   Complaint Filed by Licensing/Regulatory Agency: <i v-if="subcontractor.has_official_complaint === null || subcontractor.has_official_complaint === undefined" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>(EEOC, SCHAC, etc.)</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_official_complaint" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_official_complaint" class="text-danger">
                                        <span v-for="error in errors.has_official_complaint">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_terminated_contract }">
                                   Contract Terminated for Cause: <i v-if="subcontractor.has_terminated_contract === null || subcontractor.has_terminated_contract === undefined" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>(by owner or general contractor?)</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_terminated_contract" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_terminated_contract" class="text-danger">
                                        <span v-for="error in errors.has_terminated_contract">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_failed_work }">
                                   Failed to Complete Work: <i v-if="subcontractor.has_failed_work === null || subcontractor.has_failed_work === undefined" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_failed_work" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_failed_work" class="text-danger">
                                        <span v-for="error in errors.has_failed_work">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_pending_claims }">
                                   Judgments or Claims Pending: <i v-if="subcontractor.has_pending_claims === null || subcontractor.has_pending_claims === undefined" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_pending_claims" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_pending_claims" class="text-danger">
                                        <span v-for="error in errors.has_pending_claims">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_arbitration }">
                                   Involved in Arbitration Proceedings: <i v-if="subcontractor.has_arbitration === null || subcontractor.has_arbitration === undefined" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>Past or Present</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_arbitration" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_arbitration" class="text-danger">
                                        <span v-for="error in errors.has_arbitration">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-if="subcontractor.has_official_complaint == 1 || subcontractor.has_terminated_contract == 1 || subcontractor.has_failed_work == 1 || subcontractor.has_pending_claims == 1 || subcontractor.has_arbitration == 1" class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.past_performance }">
                                    Explain All "Yes" Responses <i v-if="!subcontractor.past_performance" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>Please provide an explanation for any "Yes" answer in this Section (8)</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.past_performance" :class="{'is-invalid': errors.past_performance}" rows="3" placeholder="Explain All 'Yes' answers above, if applicable"></b-form-textarea>

                                    <span v-if="errors.past_performance" class="text-danger">
                                        <span v-for="error in errors.past_performance">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[8] && custom_field_sections[8].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[8].fields"
                                   :initialValues="(custom_fields[8] ? custom_fields[8] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(8, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(8)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Past Performance
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="reference-information">
                        <h4 class="card-header">Section 9: References</h4>
                        <div class="card-body">
                            <references-list :subcontractorId="subcontractor.subcontractor_id" :editMode="true" :sectionSettings="sub_settings[9]"></references-list>

                            <h6 style="margin-top: 50px; margin-bottom: 5px;">{{ $store.state.user.account_name }} References:</h6>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_previous_projects }">
                                   Has your company performed work for {{ $store.state.user.account_name }} within the last {{ $store.state.user.sub_years_required }} years: <i v-if="subcontractor.has_previous_projects === null || subcontractor.has_previous_projects === undefined" class="fa fa-asterisk small text-danger"></i>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_previous_projects" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_previous_projects" class="text-danger">
                                        <span v-for="error in errors.has_previous_projects">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div v-if="subcontractor.has_previous_projects == 1" class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.other_projects }">
                                   Please list all projects:
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.other_projects" rows="3" placeholder="Enter all previous projects"></b-form-textarea>

                                    <span v-if="errors.other_projects" class="text-danger">
                                        <span v-for="error in errors.other_projects">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[9] && custom_field_sections[9].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[9].fields"
                                   :initialValues="(custom_fields[9] ? custom_fields[9] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(9, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(9)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save References
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card mb-5" id="eeo-information">
                        <h4 class="card-header">Section 10: EEO &amp; ELIGIBILITY TO WORK</h4>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_eeo_policy }">
                                   (1)(a) Written EEO Policy: <i v-if="subcontractor.has_eeo_policy === null || subcontractor.has_eeo_policy === undefined" class="fa fa-asterisk small text-danger"></i><br>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_eeo_policy" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_eeo_policy" class="text-danger">
                                        <span v-for="error in errors.has_eeo_policy">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_harassment_policy }">
                                   (1)(b) Anti-Harassment Policy: <i v-if="subcontractor.has_harassment_policy === null || subcontractor.has_harassment_policy === undefined" class="fa fa-asterisk small text-danger"></i><br>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_harassment_policy" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_harassment_policy" class="text-danger">
                                        <span v-for="error in errors.has_harassment_policy">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_complaint_procedure }">
                                   (1)(c) Employee Complaint Procedure: <i v-if="subcontractor.has_complaint_procedure === null || subcontractor.has_complaint_procedure === undefined" class="fa fa-asterisk small text-danger"></i><br>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_complaint_procedure" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_complaint_procedure" class="text-danger">
                                        <span v-for="error in errors.has_complaint_procedure">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_eeo_officer }">
                                   (1)(d) Designated EEO Officer: <i v-if="subcontractor.has_eeo_officer === null || subcontractor.has_eeo_officer === undefined" class="fa fa-asterisk small text-danger"></i><br>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_eeo_officer" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_eeo_officer" class="text-danger">
                                        <span v-for="error in errors.has_eeo_officer">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row" v-if="subcontractor.has_eeo_officer == 1">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.eeo_officer_credentials }">
                                   EEO Officer Credentials: <i v-if="!subcontractor.eeo_officer_credentials" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>Please provide the EEO Office Name, Title, Phone and Email</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.eeo_officer_credentials" :class="{'is-invalid': errors.eeo_officer_credentials}" rows="4"></b-form-textarea>

                                    <span v-if="errors.eeo_officer_credentials" class="text-danger">
                                        <span v-for="error in errors.eeo_officer_credentials">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.eeo_file_id }">
                                   Copy of EEO Policies:
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <template v-if="subcontractor.eeo_file_id">
                                       <div class="mb-2">
                                           <div>{{ subcontractor.eeo_file.file_name }}</div>
                                           <span v-if="!subcontractor.eeo_file.download_only">
                                               <a :href="`/files/view/${subcontractor.eeo_file.file_id}`" target="_blank">View</a>&nbsp;|&nbsp;
                                           </span>
                                           <a :href="`/files/download/${subcontractor.eeo_file.file_id}`" target="_blank">Download</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" @click="deleteEEOFile" class="text-danger">Delete</a>&nbsp;|&nbsp;

                                           <a href="javascript:void(0)" v-on:click="uploadeEEOFile=true">Upload Copy of EEO Policies</a>
                                       </div>
                                    </template>
                                    <file-uploader v-if="uploadeEEOFile || !subcontractor.eeo_file_id"
                                        assetType="SubPreQual"
                                       :assetId="subcontractor.subcontractor_id"
                                       :postUrl="`subcontractors/files/eeo/${subcontractor.subcontractor_id}`"
                                       @fileUploaded="updateEEOFile" />

                                    <span v-if="errors.eeo_file_id" class="text-danger">
                                        <span v-for="error in errors.eeo_file_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_verify_employees }">
                                   (2)(a) Verify Employee Eligibility: <i v-if="subcontractor.has_verify_employees === null || subcontractor.has_verify_employees === undefined" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>to work in the U.S. (I-9 Compliance)?</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_verify_employees" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_verify_employees" class="text-danger">
                                        <span v-for="error in errors.has_verify_employees">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.has_compliance_records }">
                                   (2)(b) Maintain of Compliance Records: <i v-if="subcontractor.has_compliance_records === null || subcontractor.has_compliance_records === undefined" class="fa fa-asterisk small text-danger"></i><br>
                                   <small>to confirm compliance with I-9 Guidelines?</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-radio-group v-model="subcontractor.has_compliance_records" plain class="mt-2">
                                        <b-form-radio value="1">Yes</b-form-radio>
                                        <b-form-radio value="0">No</b-form-radio>
                                    </b-form-radio-group>

                                    <span v-if="errors.has_compliance_records" class="text-danger">
                                        <span v-for="error in errors.has_compliance_records">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.e_verify_id }">
                                   (2)(c) E-Verify ID Number:<br>
                                   <small>(if enrolled - more information can be found at www.e-verify.gov)</small>
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-input type="text" v-model="subcontractor.e_verify_id" :class="{'is-invalid': errors.e_verify_id}" placeholder="Enter ID Number" />

                                    <span v-if="errors.e_verify_id" class="text-danger">
                                        <span v-for="error in errors.e_verify_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <template v-if="custom_field_sections[10] && custom_field_sections[10].fields.length > 0">
                               <hr />

                               <custom-field-form
                                   :fields="custom_field_sections[10].fields"
                                   :initialValues="(custom_fields[10] ? custom_fields[10] : null)"
                                   :errors="errors"
                                   labelSize="col-lg-3 col-md-3 col-sm-4"
                                   fieldSize="col-sm-8"
                                   @customFieldsChanged="setCustomFieldValues(10, ...arguments)" />
                            </template>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress(10)" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save EEO &amp; ELIGIBILITY TO WORK
                                </template>
                            </b-button>
                        </div>
                    </div>
                    <div class="card" id="additional-remarks">
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right text-muted': true, 'text-danger': errors.additional_notes }">
                                   Additional Notes:
                               </label>
                                <div class="col-md-8 col-sm-8">

                                    <b-form-textarea v-model="subcontractor.additional_notes" :class="{'is-invalid': errors.additional_notes}" rows="4" placeholder="Enter Any Additional Notes or Comments"></b-form-textarea>

                                    <span v-if="errors.additional_notes" class="text-danger">
                                        <span v-for="error in errors.additional_notes">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="card-footer">
                            <b-button size="sm" variant="secondary" v-on:click="saveProgress('additional')" :disabled="saving">
                                <template v-if="saving">
                                    Saving <i class="fa fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    <i class="fa fa-save"></i> Save Additional Notes
                                </template>
                            </b-button>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 d-lg-block d-md-none">
                    <affix-nav-helper>
                        <div class="card">
                            <div class="card-body">
                                <b-nav-item @click="scrollIntoView" href="#general-information">Section 1: General Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#company-information">Section 2: Company Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#work-performed">Section 3: Work Performed</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#financial-information">Section 4: Financial Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#safety-information">Section 5: Safety Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#insurance-information">Section 6: Insurance Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#bonding-information">Section 7: Bonding Information</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#past-performance">Section 8: Past Performance</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#reference-information">Section 9: References</b-nav-item>
                                <b-nav-item @click="scrollIntoView" href="#eeo-information">Section 10: EEO &amp; Eligibility to Work</b-nav-item>
                            </div>
                        </div>
                        <router-link
                            :to="reviewSubmitURL"
                            class="btn btn-sm btn-success mt-4"
                            :disabled="subcontractor.status_id == 2 || subcontractor.status_id == 8"
                            tag="button"
                            :title="(subcontractor.status_id == 2 || subcontractor.status_id == 8) ? 'Your pre-qualification is already in review. Just hit any of the save buttons and your data will be updated.' : ''"
                            style="max-width: 160px;">
                            Review & Submit <i class="fa fa-angle-right"></i>
                        </router-link>
                    </affix-nav-helper>
                </div>
            </div>
            <hr/>

            <!-- In Review or Renewal In Review -->
            <div class="alert alert-warning" v-if="subcontractor.status_id == 2 || subcontractor.status_id == 8">
                Your pre-qualification is already in review. Just hit any of the save buttons and your data will automatically be updated. You do not need to re-submit your pre-qualification.
            </div>

            <router-link
                :to="reviewSubmitURL"
                class="btn btn-sm btn-success"
                :disabled="subcontractor.status_id == 2 || subcontractor.status_id == 8"
                tag="button"
                :title="(subcontractor.status_id == 2 || subcontractor.status_id == 8) ? 'Your pre-qualification is already in review. Just hit any of the save buttons and your data will be updated.' : ''">
                Review & Submit <i class="fa fa-angle-right"></i>
            </router-link>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                uploadPrequalifiedFile: false,
                uploadIRSForm: false,
                uploadProgramFile: false,
                uploadEmrFile: false,
                uploadInsuranceFile: false,
                uploadBondingFile: false,
                uploadeEEOFile: false,
                id: this.$route.params.id,
                errors: {},
                subcontractor: {},
                trades: [],
                officeTypes: [],
                companyStructures: [],
                areas: [],
                registrations: [],
                irs_w9: [],
                fieldTrainings: [],
                substanceTestings: [],
                state_list: [],
                settings: {},

                sub_settings: {},
                custom_fields: {},
                custom_field_sections: {},

                sub_registrations: [],
                edit_state_registration: {},
                edit_state_reg_file: null,
                edit_state_reg_loading: false,

                editMode: true,
                checkedOut: false,
                checkedOutUser: null,
                checkedOutUntil: null,
                business_start_date: null,
                bonding_start_date: null,

                //Auto-Saving Helpers
                timeTilRefresh: null,
                refreshIntervalId: null,
                refreshTimeInMiutes: null,
            }
        },

        watch: {
            custom_fields: {
                deep: true,
                handler(newVal, oldVal){
                    //Set the helper
                    this.setAutoSave(2);
                },
            },

            subcontractor: {
                deep: true,
                handler(newVal, oldVal){
                    //Set the helper
                    this.setAutoSave(2);
                },
            },

            timeTilRefresh(newVal, oldVal){
                //Check for Time = 0, then REfresh
                if(newVal === 0)
                {
                    //Fire it
                    this.autoSave();
                }
            },
        },

        computed: {
            customFieldFileTypes(){
                //Loop and build
                var fields = [];

                //Loop and Check ALL
                var keys = Object.keys(this.custom_field_sections);

                //Main Loop
                for (var i = 0; i < keys.length; i++)
                {
                    //Loop Through all the field
                    for (var j = 0; j < this.custom_field_sections[keys[i]].fields.length; j++)
                    {
                        //Check it
                        if(this.custom_field_sections[keys[i]].fields[j].type == 'File')
                        {
                            //SEtup the file
                            var fileField = this.custom_field_sections[keys[i]].fields[j];

                            //Set the Section as a helper
                            fileField.section = keys[i];

                            //Push it
                            fields.push(fileField);
                        }
                    }
                }

                //Send it
                return fields;
            },

            //Major Body Part Options
            stateRegistrationOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select State --'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.registrations.length; i++)
                {
                    //Push onto options
                    options.push({value: this.registrations[i].state_id, text: this.registrations[i].state_name});
                }

                //Send it
                return options;
            },

            reviewSubmitURL(){
                //Send it
                return '/subcontractors/review' + (this.$store.state.user.subcontractor_id != this.subcontractor.subcontractor_id ? '/' + this.subcontractor.subcontractor_id : '');
            },
        },

        methods: {
            scrollIntoView (evt) {
                evt.preventDefault()
                const href = evt.target.getAttribute('href')
                const el = href ? document.querySelector(href) : null

                if (el)
                {
                    window.scrollTo(0,el.offsetTop+160)
                }
            },

            getSubcontractor() {
                //Get the data
                this.$http
                    .get('subcontractors/qualification/edit' + (this.id ? '/' + this.id  :''))
                    .then(response => {
                        //Check if someone already has this checked out
                        if (response.data.checked_out_user != undefined)
                        {
                            this.checkedOut = true;
                            this.checkedOutUser = response.data.checked_out_user.FirstName + ' ' + response.data.checked_out_user.LastName;
                            this.checkedOutUntil = response.data.checked_out_until;
                            this.loading = false;

                            return;
                        }
                        //Set all the data
                        this.subcontractor = response.data.subcontractor;

                        //Commit the shared resources to the store
                        this.$store.commit('setBids', response.data.bids);
                        this.$store.commit('setDesignations', response.data.designations);
                        this.$store.commit('setLicenses', response.data.licenses);
                        this.$store.commit('setSafetyYears', response.data.oshaYears);
                        this.$store.commit('setCitations', response.data.citations);
                        this.$store.commit('setReferences', response.data.references);
                        this.$store.commit('setStates', response.data.states);

                        this.trades = response.data.trades;
                        this.officeTypes = response.data.officeTypes;
                        this.companyStructures = response.data.companyStructures;
                        this.areas = response.data.areas;
                        this.registrations = response.data.registrations;
                        this.fieldTrainings = response.data.fieldTrainings;
                        this.substanceTestings = response.data.substanceTestings;
                        this.settings = response.data.settings;
                        this.state_list = response.data.states;
                        this.sub_registrations = response.data.sub_registrations;

                        this.editMode = (this.subcontractor.status_id == 1);

                        if (this.subcontractor.business_start_date)
                        {
                            this.business_start_date = moment(this.subcontractor.business_start_date, 'YYYY-MM-DD').format('MM/YYYY');
                        }
                        if (this.subcontractor.bonding_start_date)
                        {
                            this.bonding_start_date = moment(this.subcontractor.bonding_start_date, 'YYYY-MM-DD').format('MM/DD/YYYY');
                        }

                        //Push the Settings
                        if(response.data.sub_settings)
                        {
                            //Set it
                            this.sub_settings = response.data.sub_settings;
                        }

                        //Check Custom Field Sections
                        this.custom_field_sections = response.data.sections;
                        this.custom_fields = response.data.custom_fields;

                        // This has VERY specific guidelines on what it can do
                        // No popups, no custom messages
                        window.onbeforeunload = (e) => {
                            this.removeCheckOut();
                            if (this.$store.state.user.subcontractor_id)
                            {
                                    setTimeout(() => {
                                        this.$toastr.warning('Make sure to hit "Review & Submit" when you\'re finished');
                                    }, 1)
                                    return ''
                            }
                        };

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            saveProgress(section_num){
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Check Dates
                if (this.business_start_date)
                {
                    this.subcontractor.business_start_date = moment(this.business_start_date, 'MM/YYYY').format('YYYY-MM-DD');
                }

                //Check Dates
                if (this.bonding_start_date)
                {
                    this.subcontractor.bonding_start_date = moment(this.bonding_start_date, 'MM/DD/YYYY').format('YYYY-MM-DD');
                }

                //Set the Custom Fields
                this.subcontractor.custom_fields = typeof this.custom_fields[section_num] != 'undefined' ? this.custom_fields[section_num] : {};

                //Build the custom field Files
                var files = {};

                //Loop and Build
                for (var i = this.customFieldFileTypes.length - 1; i >= 0; i--)
                {
                    //Push onto the fields
                    if(this.subcontractor.custom_fields[this.customFieldFileTypes[i].id])
                    {
                        //Set the field type
                        files['custom_field_file_'+this.customFieldFileTypes[i].id] = this.subcontractor.custom_fields[this.customFieldFileTypes[i].id];

                        //Delete from the incident
                        //delete this.incident.custom_fields[this.customFieldFileTypes[i].id];
                    }
                }

                //Send it
                this.$http
                .postWithFiles('subcontractors/save/'+section_num+'/' + this.subcontractor.subcontractor_id, this.subcontractor, files)
                .then(response => {
                    //WHY are we changing this to 1?
                    //This is why we need fucking comments in code.
                    //Previous developers left current devs up shits creek without a paddled trying
                    //to figure out WHY this happens.
                    //Commenting this out for now as this RE-Activates the Submit Button
                    //this.subcontractor.status_id = 1;

                    if (response.data.errors)
                    {
                        //Display validation errors
                        this.errors = response.data.errors
                        this.$toastr.warning('Pre-Qualification Saved With Errors');
                    }
                    else
                    {
                        this.$toastr.success('Pre-Qualification Saved');
                    }
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            //In order to prevent a FLOOD or requests, which happens on any text/textarea field,
            //We are going to flag the page as auto-save needed and wait X seconds. This basically
            //set a countdown timer and gets reset everytime this function gets called. When the timer
            //hits 0, then the function runs. This has the effect of only running 1 api when the user
            //finishes typing or entering data
            setAutoSave(timeInSeconds){
                //Store the setting
                this.refreshTimeInMiutes = timeInSeconds;

                //Check for an existing interval
                if(this.refreshIntervalId)
                {
                    //Clear the Interval
                    clearInterval(this.refreshIntervalId);
                }

                //Check for Null
                if(timeInSeconds == null)
                {
                    //Disable it
                    this.refreshIntervalId = null;
                    this.timeTilRefresh = null;

                    //Stop
                    return;
                }

                //Send it
                this.timeTilRefresh = this.refreshTimeInMiutes;

                //Set the interval
                this.refreshIntervalId = setInterval(() => {
                    //Decremebnt the time Til Refresh
                    this.timeTilRefresh--;
                }, 1000);
            },

            autoSave(){
                //Check Dates
                if (this.business_start_date)
                {
                    this.subcontractor.business_start_date = moment(this.business_start_date, 'MM/YYYY').format('YYYY-MM-DD');
                }

                //Check Dates
                if (this.bonding_start_date)
                {
                    this.subcontractor.bonding_start_date = moment(this.bonding_start_date, 'MM/DD/YYYY').format('YYYY-MM-DD');
                }

                //Set the Custom Fields
                this.subcontractor.custom_fields = this.custom_fields;

                //Build the custom field Files
                var files = {};

                //Loop and Build
                for (var i = this.customFieldFileTypes.length - 1; i >= 0; i--)
                {
                    //Push onto the fields
                    if(this.subcontractor.custom_fields[this.customFieldFileTypes[i].section][this.customFieldFileTypes[i].id])
                    {
                        //Set the field type
                        files['custom_field_file_'+this.customFieldFileTypes[i].id] = this.subcontractor.custom_fields[this.customFieldFileTypes[i].id];
                    }
                }

                //Send it
                this.$http
                .postWithFiles('subcontractors/auto-save/'+ this.subcontractor.subcontractor_id, this.subcontractor, files)
                .then(response => {
                    //Do nothing
                })
                .catch(error => {
                    //Do nothing
                })
                .then(response => {

                });
            },

            updatePrequalifiedFile(payload){
                //Alert the user
                this.$toastr.success('Prequalified 3rd Party Certification Uploaded');

                //Set the value
                this.subcontractor.prequalified_file = payload.serverResponse;
                this.subcontractor.prequalified_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadPrequalifiedFile = false;
            },

            deletePrequalifiedFile() {
                this.$http.post(`subcontractors/files-delete/prequalified/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('Prequalified 3rd Party Certification Removed');
                    this.uploadPrequalifiedFile=true
                    this.subcontractor.prequalified_file = null;
                    this.subcontractor.prequalified_file_id = null;
                });
            },

            updateIRSForm(payload){
                //Alert the user
                this.$toastr.success('IRS W9 Form Uploaded');

                //Set the value
                this.subcontractor.irs_w9 = payload.serverResponse;
                this.subcontractor.irsw9_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadIRSForm = false;
            },

            deleteIRSForm() {
                this.$http.post(`subcontractors/files-delete/irsw9/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('IRS W9 Form Removed');
                    this.uploadIRSForm=true
                    this.subcontractor.irsw9 = null;
                    this.subcontractor.irsw9_id = null;
                });
            },

            updateProgramFile(payload){
                //Alert the user
                this.$toastr.success('Safety & Health Program Uploaded');

                //Set the value
                this.subcontractor.program_file = payload.serverResponse;
                this.subcontractor.program_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadProgramFile = false;
            },

            deleteProgramFile() {
                this.$http.post(`subcontractors/files-delete/program/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('Safety & Health Program Removed');
                    this.uploadProgramFile=true
                    this.subcontractor.program_file = null;
                    this.subcontractor.program_file_id = null;
                });
            },

            updateEmrFile(payload){
                //Alert the user
                this.$toastr.success('EMR Letter Uploaded');

                //Set the value
                this.subcontractor.emr_file = payload.serverResponse;
                this.subcontractor.emr_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadEmrFile = false;
            },

            deleteEmrFile() {
                this.$http.post(`subcontractors/files-delete/emr/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('EMR Letter Removed');
                    this.uploadEmrFile=true
                    this.subcontractor.emr_file = null;
                    this.subcontractor.emr_file_id = null;
                });
            },

            updateInsuranceFile(payload){
                //Alert the user
                this.$toastr.success('Certificate of Insurance Uploaded');

                //Set the value
                this.subcontractor.insurance_file = payload.serverResponse;
                this.subcontractor.insurance_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadInsuranceFile = false;
            },

            deleteInsuranceFile() {
                this.$http.post(`subcontractors/files-delete/insurance/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('Certificate of Insurance Removed');
                    this.uploadInsuranceFile=true
                    this.subcontractor.insurance_file = null;
                    this.subcontractor.insurance_file_id = null;
                });
            },

            updateBondingLetter(payload){
                //Alert the user
                this.$toastr.success('Bonding Letter Uploaded');

                //Set the value
                this.subcontractor.bonding_file = payload.serverResponse;
                this.subcontractor.bonding_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadBondingFile = false;
            },

            deleteBondingLetter() {
                this.$http.post(`subcontractors/files-delete/bonding/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('Bonding Letter Removed');
                    this.uploadBondingFile=true
                    this.subcontractor.bonding_file = null;
                    this.subcontractor.bonding_file_id = null;
                });
            },

            updateEEOFile(payload){
                //Alert the user
                this.$toastr.success('EEO Policies Uploaded');

                //Set the value
                this.subcontractor.eeo_file = payload.serverResponse;
                this.subcontractor.eeo_file_id = payload.serverResponse.file_id;

                //Hide the uploader
                this.uploadeEEOFile = false;
            },

            deleteEEOFile() {
                this.$http.post(`subcontractors/files-delete/eeo/${this.id}`, {file: []})
                .then(response => {
                    this.$toastr.success('EEO Policies Removed');
                    this.uploadeEEOFile=true
                    this.subcontractor.eeo_file = null;
                    this.subcontractor.eeo_file_id = null;
                });
            },

            removeCheckOut(admin_request = false) {
                //Send it
                this.$http.get('subcontractors/remove-check-out/' + this.id + (admin_request ? `/1` : ''))
                .then(response => {
                    if (admin_request == true)
                    {
                        this.$router.go();
                    }
                });
            },

            setCustomFieldValues(section, fields){
                //Set it
                this.custom_fields[section] = fields;

                //Fire it
                //this.saveProgress(section);
            },

            settingIsOn(section_num, setting_key){
                //Check if setting is turned on
                if(this.sub_settings[section_num][setting_key] !== undefined && this.sub_settings[section_num][setting_key] === 1)
                {
                    return true;
                }

                //Defaul
                return false;
            },

            addStateRegistration(){
                //Build new
                var reg = {
                    id: null,
                    subcontractor_id: this.subcontractor.subcontractor_id,
                    state_id: null,
                    registration_notes: null,
                };

                //Push it
                this.sub_registrations.push(reg);

                //Set it
                this.edit_state_registration = reg;
            },

            editSubStateRegistration(registration){
                //Set it
                this.edit_state_registration = registration;
            },

            cancelStateRegistration(i){
                //Check for ID
                if(this.edit_state_registration.id == null)
                {
                    //Splice it
                    this.sub_registrations.splice(i, 1);
                }

                //Reset
                this.edit_state_registration = {};
            },

            updateStateRegistration(i){
                //Loading
                this.edit_state_reg_loading = true;

                //SEtup files
                var files = {};

                //Check for file
                if(this.edit_state_reg_file)
                {
                    //Append it
                    files['file'] = this.edit_state_reg_file;
                }

                //Send it
                this.$http
                    .postWithFiles('subcontractors/state-registration/' + this.subcontractor.subcontractor_id, this.edit_state_registration, files)
                    .then(response => {
                        //Reset
                        this.edit_state_registration = {};

                        //Upsert the Sub Registration
                        this.sub_registrations.splice(i, 1, response.data);
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.edit_state_reg_loading = false;
                    });
            },

            deleteSubStateRegistration(reg, i){
                //Confirm
                if(!confirm('Are you sure you want to delete this State Registration? This cannot be undone'))
                {
                    return;
                }

                //Loading
                this.edit_state_reg_loading = true;

                //Send it
                this.$http
                    .post('subcontractors/state-registration/' + this.subcontractor.subcontractor_id + '/delete/' + reg.id)
                    .then(response => {
                        //Upsert the Sub Registration
                        this.sub_registrations.splice(i, 1);

                        //Alert
                        this.$toastr.success('State Registration Deleted');
                    })
                    .catch(error => {

                    })
                    .then(response => {
                        //SHow loading
                        this.edit_state_reg_loading = false;
                    });
            },
        },

        mounted() {
            //Get the subcontractors
            this.getSubcontractor();
        },

        beforeRouteLeave(to, from, next)
        {
            //Check if they are leaving and not going to Review
            if (this.$store.state.user.subcontractor_id && to.path.indexOf('subcontractors/review') === -1)
            {
                if (confirm('Are you sure you want to leave without reviewing and submitting your data?'))
                {
                    this.removeCheckOut();
                    window.onbeforeunload = function () {
                      // blank function do nothing
                    }
                    next();
                }
                else
                {
                    this.$toastr.warning('Make sure to hit "Review & Submit" when you\'re finished');
                }
            }
            else
            {
                this.removeCheckOut();
                next();
            }
        },
    }
</script>
